/**
 * E002-T040: Column Selector Component
 * E007-T006: Attachment Column Indicator
 *
 * Collapsible panel with checkboxes to select which columns to include in the payload.
 * Replaces the complex MappingEditor modal with a simpler UX.
 *
 * Attachment columns are visually marked to indicate that files will be
 * transferred as download URLs in the webhook payload.
 */

import { Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { AttachmentIcon, SearchIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  type ColumnInfo,
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../../services/database-discovery';
import { CollapsibleSection } from './collapsible-section';
import {
  SectionEmpty,
  SectionSearchBox,
  sectionSearchInputClassName,
} from './section-layout';
import { TypeChip } from './type-chip';

/** sl-m379: Column group from a secondary/related table */
export interface SecondaryColumnGroup {
  tableId: string;
  label: string;
  columns: ColumnInfo[];
}

interface ColumnSelectorProps {
  /** Source table block ID to get columns from */
  sourceTableId: string | null;
  /** Currently selected column IDs */
  selectedColumns: string[];
  /** Callback when selection changes */
  onChange: (selectedColumns: string[]) => void;
  /** Callback when (i) icon is clicked - shows section coachmark */
  onInfoClick?: () => void;
  /** sl-m379: Additional column groups from secondary/related tables */
  secondaryColumnGroups?: SecondaryColumnGroup[];
}

export const ColumnSelector: FC<ColumnSelectorProps> = ({
  sourceTableId,
  selectedColumns,
  onChange,
  onInfoClick,
  secondaryColumnGroups,
}) => {
  const t = useI18n();
  const discoveryService = useService(DatabaseDiscoveryService);
  const databases = useLiveData(discoveryService.databases$);
  const [searchQuery, setSearchQuery] = useState('');

  // Find columns for the selected source table
  const sourceTable = sourceTableId
    ? databases.find((db: DatabaseInfo) => db.blockId === sourceTableId)
    : null;
  const leadColumns = useMemo(
    () => sourceTable?.columns ?? [],
    [sourceTable?.columns]
  );

  // sl-m379: Merge lead + secondary columns into a single flat list with group labels.
  // sl-i6t9-fix: Each column gets a _selectionKey (tableId:columnId) to avoid ID collisions
  // (e.g. BlockSuite title columns share the same ID across databases).
  const columns = useMemo(() => {
    const leadId = sourceTableId ?? 'lead';
    if (!secondaryColumnGroups?.length)
      return leadColumns.map((c: ColumnInfo) => ({
        ...c,
        _tableId: leadId,
        _selectionKey: `${leadId}:${c.id}`,
      }));
    const leadLabel = sourceTable?.databaseTitle ?? 'Lead';
    const all: (ColumnInfo & {
      groupLabel?: string;
      _tableId: string;
      _selectionKey: string;
    })[] = leadColumns.map((c: ColumnInfo) => ({
      ...c,
      groupLabel: leadLabel,
      _tableId: leadId,
      _selectionKey: `${leadId}:${c.id}`,
    }));
    for (const group of secondaryColumnGroups) {
      for (const col of group.columns) {
        all.push({
          ...col,
          groupLabel: group.label,
          _tableId: group.tableId,
          _selectionKey: `${group.tableId}:${col.id}`,
        });
      }
    }
    return all;
  }, [
    leadColumns,
    secondaryColumnGroups,
    sourceTable?.databaseTitle,
    sourceTableId,
  ]);

  // Filter columns by search query
  const filteredColumns = useMemo(() => {
    if (!searchQuery.trim()) return columns;
    const query = searchQuery.toLowerCase();
    return columns.filter(
      (col: ColumnInfo) =>
        col.name.toLowerCase().includes(query) ||
        col.type.toLowerCase().includes(query)
    );
  }, [columns, searchQuery]);

  // Toggle single column using composite selection key
  const handleToggleColumn = useCallback(
    (selectionKey: string) => {
      if (selectedColumns.includes(selectionKey)) {
        onChange(selectedColumns.filter(id => id !== selectionKey));
      } else {
        onChange([...selectedColumns, selectionKey]);
      }
    },
    [selectedColumns, onChange]
  );

  // Select/Deselect all (only visible columns after filter)
  const handleSelectAll = useCallback(() => {
    const visibleKeys = filteredColumns.map(
      (col: ColumnInfo & { _selectionKey?: string }) =>
        col._selectionKey ?? col.id
    );
    const allSelected = visibleKeys.every((key: string) =>
      selectedColumns.includes(key)
    );

    if (allSelected) {
      // Deselect all visible
      onChange(selectedColumns.filter(id => !visibleKeys.includes(id)));
    } else {
      // Select all visible (merge with existing)
      const newSelection = [...new Set([...selectedColumns, ...visibleKeys])];
      onChange(newSelection);
    }
  }, [filteredColumns, selectedColumns, onChange]);

  // Count selected
  const selectedCount = selectedColumns.length;
  const totalCount = columns.length;

  // Badge for count
  const badge = {
    label: `${selectedCount} / ${totalCount}`,
    status: selectedCount > 0 ? ('active' as const) : ('incomplete' as const),
  };

  // Show placeholder if no source table
  if (!sourceTableId) {
    return (
      <CollapsibleSection
        title={t['com.streamlain.streamlain.input.fields']()}
        subtitle={t['com.streamlain.streamlain.input.fields-subtitle']()}
        onInfoClick={onInfoClick}
        defaultCollapsed
      >
        <SectionEmpty>
          {t['com.streamlain.streamlain.input.select-source-first']()}
        </SectionEmpty>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      title={t['com.streamlain.streamlain.input.fields']()}
      subtitle={t['com.streamlain.streamlain.input.fields-subtitle']()}
      badge={badge}
      onInfoClick={onInfoClick}
      defaultCollapsed
    >
      {/* Search bar */}
      <SectionSearchBox
        icon={
          <SearchIcon
            style={{
              width: '16px',
              height: '16px',
              color: 'var(--streamlain-text-secondary-color)',
            }}
          />
        }
      >
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t['com.streamlain.streamlain.input.search-columns']()}
          className={sectionSearchInputClassName}
        />
      </SectionSearchBox>

      {/* Select All / Deselect All */}
      {filteredColumns.length > 0 && (
        <button
          onClick={handleSelectAll}
          data-coachmark-target="fields-select-all"
          style={{
            padding: '6px 12px',
            fontSize: '12px',
            color: 'var(--streamlain-v2-text-link)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            marginBottom: '8px',
          }}
        >
          {filteredColumns.every(
            (col: ColumnInfo & { _selectionKey?: string }) =>
              selectedColumns.includes(col._selectionKey ?? col.id)
          )
            ? t['com.streamlain.streamlain.input.deselect-all']()
            : t['com.streamlain.streamlain.input.select-all']()}
        </button>
      )}

      {/* Column list */}
      {columns.length === 0 ? (
        <SectionEmpty>
          {t['com.streamlain.streamlain.input.no-columns']()}
        </SectionEmpty>
      ) : filteredColumns.length === 0 ? (
        <SectionEmpty>
          {t['com.streamlain.streamlain.input.no-columns-match']({
            query: searchQuery,
          })}
        </SectionEmpty>
      ) : (
        <div
          data-coachmark-target="fields-column-list"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            maxHeight: '300px',
            overflowY: 'auto',
          }}
        >
          {filteredColumns.map(
            (
              column: ColumnInfo & {
                groupLabel?: string;
                _tableId?: string;
                _selectionKey?: string;
              },
              idx: number
            ) => {
              const selectionKey = column._selectionKey ?? column.id;
              const isSelected = selectedColumns.includes(selectionKey);
              const isAttachment = column.type.toLowerCase() === 'attachment';
              // sl-m379: Show group header when transitioning to a new table
              const prevGroup =
                idx > 0
                  ? (
                      filteredColumns[idx - 1] as ColumnInfo & {
                        groupLabel?: string;
                      }
                    ).groupLabel
                  : undefined;
              const showGroupHeader =
                column.groupLabel && column.groupLabel !== prevGroup;
              const uniqueKey = selectionKey;
              return (
                <div key={uniqueKey}>
                  {showGroupHeader && (
                    <div
                      style={{
                        padding: '6px 12px 2px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'var(--streamlain-text-secondary-color)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        borderTop:
                          idx > 0
                            ? '1px solid var(--streamlain-border-color)'
                            : undefined,
                        marginTop: idx > 0 ? '4px' : undefined,
                      }}
                    >
                      {column.groupLabel}
                    </div>
                  )}
                  <div
                    onClick={() => handleToggleColumn(selectionKey)}
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: '10px',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? 'var(--streamlain-hover-color)'
                        : 'transparent',
                      transition: 'background-color 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor =
                          'var(--streamlain-hover-color-filled)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {/* Custom Checkbox */}
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        minWidth: '16px',
                        flexShrink: 0,
                        borderRadius: '4px',
                        border: isSelected
                          ? '2px solid var(--streamlain-primary-color)'
                          : '2px solid var(--streamlain-border-color)',
                        backgroundColor: isSelected
                          ? 'var(--streamlain-primary-color)'
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {isSelected && (
                        <svg
                          width="10"
                          height="8"
                          viewBox="0 0 10 8"
                          fill="none"
                          style={{ flexShrink: 0 }}
                        >
                          <path
                            d="M1 4L3.5 6.5L9 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    {/* E007-T006: Attachment indicator with tooltip */}
                    {isAttachment && (
                      <Tooltip
                        content={t[
                          'com.streamlain.streamlain.input.attachment-tooltip'
                        ]()}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: '#f97316',
                            flexShrink: 0,
                          }}
                        >
                          <AttachmentIcon style={{ width: 14, height: 14 }} />
                        </span>
                      </Tooltip>
                    )}
                    <span
                      style={{
                        flex: 1,
                        fontSize: '13px',
                        color: 'var(--streamlain-text-primary-color)',
                      }}
                    >
                      {column.name}
                    </span>
                    <TypeChip type={column.type} />
                  </div>
                </div>
              );
            }
          )}
        </div>
      )}
    </CollapsibleSection>
  );
};
